<template>
  <div
    ref="featuredProducts"
    class="products-featured"
  >
    <heading
      v-if="headingText"
      class="products-featured__heading"
      :type="headingType"
      :text="headingText"
    />

    <product-grid-simple
      v-if="currentDisplayType === 'grid'"
      :product-card-use-packshot="usePackshots"
      :products="products"
      :section-id="liquid.sectionId"
      :tracking-parameters="trackingParameters"
    />

    <product-carousel
      v-else
      :product-card-use-packshot="usePackshots"
      :products="products"
      :section-id="liquid.sectionId"
      :tracking-parameters="trackingParameters"
      :slides-per-view="slidesPerView"
    />
  </div>
</template>

<script>

/**
 * Section: Featured Products
 * ------------------------------------------------------------------------------
 * - Displays specifically selected products in a grid or carousel.
 *
 * @param {String} [headingText] - Heading Text to display.
 * @param {String} [headingType] - Theme Settings for h1-h6 values.
 * @param {Array} productHandles - Array of product handles
 * @param {String} [liquid.sectionId] - Section's Liquid ID.
 * @param {String} [displayType] - Mobile: Product View/Display type, currently either `grid` or `carousel`.
 * @param {String} [displayTypeDesktop] - Desktop: Product View/Display type, currently either `grid` or `carousel`.
 * @param {Object} [trackingParameters] - Object of parameters, typically added to events (Bloomreach, Attraqt, Google).
 * @param {String} [trackingParameters.section] - Unique name of the section.
 * @param {Boolean} [usePackshots] - If to use Packshots as Primary Product Card images.
 * @param {Boolean|Object} [slidesPerView] - An object with number of slides to display per `mobile`, `tablet` or `desktop` breakpoints.
 */
import { mapState } from 'pinia'
import { useScreenStore } from '~stores/screen'

import { isVisible, setIntersectionObserver } from '~/helpers/dom'

import Heading from '~components/heading/heading'

import { importComponent } from '~/core/mint/helpers'

export default {
  name: 'ProductsFeatured',

  components: {
    Heading,
    ProductCarousel: importComponent('product-carousel'),
    ProductGridSimple: importComponent('product-grid-simple'),
  },

  props: {
    displayType: {
      type: String,
      default: 'carousel',
    },
    displayTypeDesktop: {
      type: String,
      default: 'carousel',
    },
    headingText: {
      type: String,
      default: '',
    },
    headingType: {
      type: String,
      default: 'h2',
    },
    liquid: {
      type: Object,
      default: () => ({
        sectionId: '',
      }),
    },
    productHandles: {
      type: Array,
      default: () => [],
    },
    trackingParameters: {
      type: Object,
      default: () => ({}),
    },
    usePackshots: {
      type: Boolean,
      default: false,
    },
    slidesPerView: {
      type: [Boolean, Object],
      default: false,
    },
  },

  data() {
    return {
      state: 'loading',
      products: [
        {
          key: 0,
        },
        {
          key: 1,
        },
        {
          key: 2,
        },
        {
          key: 3,
        },
      ],
    }
  },

  computed: {

    /**
     * Map global state.
     */
    ...mapState(useScreenStore, {
      isMobile: 'mobile',
    }),

    /**
     * Calculate the current display type based on device.
     * @returns {String} `grid` or `carousel`.
     */
    currentDisplayType() {
      if (!this.isMobile) {
        return this.displayTypeDesktop
      }

      return this.displayType
    },
  },

  mounted() {
    this.initializeSectionObserver()
  },

  methods: {

    /**
     * Initializes the observer for the recently viewed section.
     * - if the section is already visible set the products immediately.
     */
    initializeSectionObserver() {
      const targetElement = this.$refs.featuredProducts

      if (isVisible(targetElement)) {
        this.setProducts()
        return
      }

      setIntersectionObserver({
        callback: this.handleSectionIntersection,
        root: targetElement.parentNode,
        rootMargin: '20px 0px',
        element: targetElement,
        threshold: 0.1,
      })
    },

    /**
     * Handle event when a section is scrolled into view.
     * @param {Array} entries - Array of Intersection Observer entries.
     * @param {Object} observer - Intersection Observer.
     */
    handleSectionIntersection(entries, observer) {
      entries.forEach(entry => {
        if (!entry.isIntersecting) {
          return
        }

        /**
         * All of the products have loaded.
         */
        if (this.state === 'ready') {
          observer.disconnect()
          return
        }

        this.setProducts()
      })
    },

    /**
     * Set the products that will be displayed in the component.
     */
    async setProducts() {
      try {

        /**
         * If in the Theme Editor, don't query and render products.
         * - GQL can also be unreliable within the Theme Editor, so prevent
         *   queries in case no products load and the section hides itself.
         */
        // if (mint.request.designMode) {
        // return
        // }

        /**
         * If on the PDP, do not show the current product in the carousel.
         */
        if (!this.productHandles.length) {
          // this.destroyComponent()
          // return
        }

        const queries = this.productHandles.map((handle) => {
          return mint.GetProduct({ handle })
        })

        const response = await Promise.allSettled(queries)

        /**
         * If the query fails, it's likely that the product isn't
         * available on the Storefront API channel.
         */
        this.products = response.map((promise) => {
          if (
            promise.status !== 'fulfilled' ||
            !promise.value?.handle
          ) {
            return false
          }

          return promise.value
        }).filter(Boolean)

        if (!this.products.length) {
          // this.destroyComponent()
          return
        }

        this.state = 'ready'
        this.handleTrackingEvents()

      } catch (error) {
        mint.log('error', 'Error fetching products', error)
      }
    },

    /**
     * Emit 'View' events to relevant event trackers (Bloomreach, Google etc.)
     */
    handleTrackingEvents() {
      // if (!mint.customer.canBeTracked) {
      //   return
      // }

      // const attributes = JSON.parse(JSON.stringify(this.trackingParameters))

      // window.console.log('Track Recently Viewed View here...', {
      //   handle: stringToHandle(attributes.section),
      //   section: attributes.section,
      // })
    },

    /**
     * Remove and unmount the component.
     * - Note, just removes the parent element.
     * - Ideally this should unmount the entire component.
     */
    destroyComponent() {
      const parentSection = document.querySelector(
        '[data-mint-section="products-featured"]',
      )

      parentSection.remove()
    },
  },
}
</script>

<style src="./products-featured.scss" lang="scss"></style>
